export const convertRublesToCredits = (rubles: number): number => {
  return Math.floor(rubles * 250);
};

export const formatCreditsNumber = (amount: number): string => {
  return new Intl.NumberFormat('ru-RU').format(amount);
};

export const formatUsdPrice = (amount: number, opts?: { whole?: boolean }): string => {
  if (!(amount > 0)) return '0';

  const digits = opts?.whole ? 0 : amount < 0.01 ? 4 : 2;
  const [int, frac] = amount.toFixed(digits).split('.');
  const grouped = formatCreditsNumber(Number(int));

  return frac ? `${grouped}.${frac}` : grouped;
};

export const formatCreditsShort = (amount: number, precision: 1 | 2 = 1): string => {
  if (amount >= 1000000) {
    const millions = amount / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${precision === 1 ? millions.toFixed(1) : Number(millions.toFixed(2))}M`;
  }
  if (amount >= 1000) {
    const thousands = amount / 1000;
    return thousands % 1 === 0 ? `${thousands}k` : `${precision === 1 ? thousands.toFixed(1) : Number(thousands.toFixed(2))}k`;
  }
  return amount.toString();
};

export const extractSkinName = (fullName: string): string => {
  if (fullName.startsWith('Sealed Graffiti')) {
    return 'Sealed Graffiti';
  }

  if (fullName.startsWith('Sticker')) {
    return 'Sticker';
  }

  const nameWithoutPrefixes = fullName.replace(/^★\s*/, '').replace(/^Souvenir\s+/, '').replace(/^StatTrak™\s+/, '');

  const parts = nameWithoutPrefixes.split('|');
  if (parts.length < 2) return fullName;

  const skinPart = parts[1].trim();
  const skinName = skinPart.replace(/\s*\([^)]*\)\s*$/, '').trim();

  return skinName;
};

export const abbreviateWearCondition = (condition: string): string => {
  const abbreviations: Record<string, string> = {
    'Factory New': 'FN',
    'Minimal Wear': 'MW',
    'Field-Tested': 'FT',
    'Well-Worn': 'WW',
    'Battle-Scarred': 'BS',
  };

  return abbreviations[condition] || condition;
};

export interface ParsedItemDetails {
  weaponName: string;
  skinName: string;
  condition: string;
  isStar: boolean;
  isStatTrak: boolean;
  isSouvenir: boolean;
}

export const parseItemDetails = (fullName: string): ParsedItemDetails => {
  const specialPrefix = fullName.startsWith('Sealed Graffiti') ? 'Graffiti'
    : fullName.startsWith('Sticker') ? 'Sticker'
    : null;

  if (specialPrefix) {
    const parts = fullName.split('|');
    let pattern = specialPrefix;
    let color = '';
    if (parts.length >= 2) {
      const rightPart = parts[1].trim();
      const match = rightPart.match(/^(.+?)\s*\((.+?)\)$/);
      if (match) {
        pattern = match[1].trim();
        color = match[2].trim();
      } else {
        pattern = rightPart;
      }
    }
    return {
      weaponName: specialPrefix,
      skinName: pattern,
      condition: color,
      isStar: false, isStatTrak: false, isSouvenir: false,
    };
  }

  const caseMatch = fullName.match(/^(.+?)\s+(Case|Capsule|Package|Collection)$/i);
  if (caseMatch) {
    return {
      weaponName: caseMatch[2],
      skinName: caseMatch[1],
      condition: '',
      isStar: false, isStatTrak: false, isSouvenir: false,
    };
  }

  const isStar = fullName.startsWith('★');
  const isStatTrak = fullName.includes('StatTrak™');
  const isSouvenir = fullName.startsWith('Souvenir');

  const nameWithoutPrefixes = fullName
    .replace(/^★\s*/, '')
    .replace(/^StatTrak™\s+/, '')
    .replace(/^Souvenir\s+/, '')
    .trim();

  const parts = nameWithoutPrefixes.split(' | ');
  if (parts.length < 2) {
    return { weaponName: nameWithoutPrefixes, skinName: '', condition: '', isStar, isStatTrak, isSouvenir };
  }

  const weaponName = parts[0].trim();
  const skinWithWear = parts[1].trim();

  const wearMatch = skinWithWear.match(/\((.*?)\)$/);
  const condition = wearMatch ? wearMatch[1] : '';
  const skinName = condition ? skinWithWear.replace(/\s*\([^)]*\)\s*$/, '').trim() : skinWithWear;

  return { weaponName, skinName, condition, isStar, isStatTrak, isSouvenir };
};

export const formatTimeRemaining = (timeRemaining: number, endedText: string = 'Ended'): string => {
  if (timeRemaining <= 0) return endedText;

  const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

